import { useContext, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import type { HouseholdsController } from "../../../../api/src/households/households.controller";
import type { UnpackResponse } from "../../../../api/src/lib";
import type { Transaction } from "../../../../api/src/transactions/lib";
import type { TransactionsController } from "../../../../api/src/transactions/transactions.controller";
import {
  deserializeModelSummaryPercents,
  useAuthenticatedPercentFetch,
} from "../percents";
import { useFirmRiskProfiles } from "../../firm/lib";
import { formatCurrency } from "../../lib/numbers";
import { useAuthenticatedFetchAllPages } from "../../lib/pagination";
import { calculatePositionsCash } from "../../lib/security";
import AccountHoldingsTable from "./AccountHoldingsTable";
import { AccountContext } from "./AccountInfo";
import AccountPerformance from "./AccountPerformance";
import AccountTaxSummary from "./AccountTaxSummary";

/**
 * Remounts the screen per account, so the transactions the page walk collected
 * for one account are never shown under another (`lib/pagination.ts`).
 */
const AccountPortfolio = () => {
  const { account } = useContext(AccountContext);

  return <AccountPortfolioOfAccount key={account?.id} />;
};

const AccountPortfolioOfAccount = () => {
  const { account } = useContext(AccountContext);

  const settledCash = calculatePositionsCash(account?.holdings ?? []);
  const estimatedUnsettledCash = account?.cashBalance ?? 0;

  const { data: dataModelSummary } = useAuthenticatedPercentFetch<
    UnpackResponse<HouseholdsController["getHouseholdModelSummary"]>
  >(
    `/v1/accounts/${account?.id}/model-summary`,
    // This route sends one `{ accountId, model }` or `null`
    // (`AccountsController.getAccountModelSummary`), not the household
    // route's array this read is typed as; its `model` weights come on 0 to
    // 100.
    (summary) =>
      deserializeModelSummaryPercents(
        summary as unknown as (typeof summary)[number] | null,
      ) as unknown as typeof summary,
  );

  // `GET /v1/transactions/account/:id` answers a page (APP-1652); the walk in
  // `useAuthenticatedFetchAllPages` keeps asking until it holds every
  // transaction `pagination.totalCount` reports.
  const { rows: transactionRows } = useAuthenticatedFetchAllPages<
    UnpackResponse<
      TransactionsController["getTransactionsForAccount"]
    >["data"][number]
  >(`/v1/transactions/account/${account?.id}`);

  // `/v1/firm/risk` answers a PAGE, not the whole collection (APP-1563).
  // `useFirmRiskProfiles` walks it; reading one response as the whole set would
  // drop every holding allocated only to a risk level past the first page, and
  // `tsc` cannot see that — a page and the whole array have the same element
  // type. `AccountPortfolio.pagination.spec.tsx` is what holds it.
  const { riskProfiles } = useFirmRiskProfiles();

  // The route returns them in display_order (`firms.service.ts`,
  // `getFirmRiskProfilesPage`), and the walk concatenates the pages in the
  // order it asked for them, so the sequence survives.
  const riskCategories = useMemo(
    () => riskProfiles.map((profile) => profile.name),
    [riskProfiles],
  );

  const transactions: Transaction[] = useMemo(
    () =>
      transactionRows.filter(
        (transaction) =>
          transaction.type === "Income" || transaction.type === "Expense",
      ),
    [transactionRows],
  );

  return typeof account === "undefined" ? null : (
    <>
      <Row>
        <Col xxl={4} xl={6} className="mb-3">
          <dl>
            <Row>
              <Col as="dt" xl={5} sm={6}>
                AUM
              </Col>
              <Col as="dd">
                {formatCurrency(account?.accountBalance ?? 0, 2)}
              </Col>
            </Row>
            <Row>
              <Col as="dt" xl={5} sm={6}>
                Est. Unsettled Cash
              </Col>
              <Col as="dd">{formatCurrency(estimatedUnsettledCash, 2)}</Col>
            </Row>
            <Row>
              <Col as="dt" xl={5} sm={6}>
                Settled Cash
              </Col>
              <Col as="dd">{formatCurrency(settledCash, 2)}</Col>
            </Row>
          </dl>
        </Col>
        <Col xl={4} className="mb-3">
          <AccountPerformance />
        </Col>
        {!account.isTaxable ? null : (
          <Col xl={4} className="mb-3">
            <AccountTaxSummary />
          </Col>
        )}
      </Row>
      <Row>
        <Col xs={12}>
          <AccountHoldingsTable
            holdings={account?.holdings ?? []}
            modelSummaries={dataModelSummary?.data ?? []}
            transactions={transactions}
            riskCategories={riskCategories}
          />
        </Col>
      </Row>
    </>
  );
};

export default AccountPortfolio;
